import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "src/components/dashboard";
import searchIcon from "src/assets/images/input/searchIcon.svg";
import addIcon from "src/assets/images/common/add.svg";
import PartInput from "src/components/inputs/partsearch";
import { useLoading } from "src/hooks";
import { auth } from "src/store/Auth";
import { theme } from "src/styles/Theme";
import { toast } from "react-toastify";
import {
  useGetAllAdminsQuery,
  useGetActivityLogsQuery,
} from "src/store/services/adminApi";
import AdminCategoryCard from "src/components/partners/AdminCards";

import AppModal from "src/components/ui/widget/Modal/Modal";
import ModalContent from "src/components/common/ModalContent";
import exportImg from "../../assets/images/modal/Icon.png";
import GlobalAnalytics from "src/components/partners/GlobalAnalytics";

import {
  Button,
  ButtonClass,
  Flex,
} from "src/components/ui";


import {
  useLazyDumpPartnersDataQuery,
} from "src/store/services/artisanApi";

// ✅ NEW: admin API (replace with your real endpoint hook)
// import { useGetAllAdminsQuery } from "src/store/services/adminApi";

const CentralRebrand = () => {
  const navigate = useNavigate();

  const {
    data: adminsResponse,
    isLoading,
    error,
    refetch,
  } = useGetAllAdminsQuery({page: 1, limit: 10, search: "centralPartnerManager"});

  const [searchQuery, setSearchQuery] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);

  const [triggerDumpArtisansData] = useLazyDumpPartnersDataQuery();

  const { authUser } = auth.use();

  const {
    loading: exportingData,
    startLoading,
    stopLoading,
  } = useLoading(false);

  useEffect(() => {
    document.title = "Partner's Page";
  }, []);

  // ===============================
  // ✅ FILTER ADMINS (centralPartnerManager)
  // ===============================
  const admins = useMemo(() => {
    return adminsResponse?.payload?.data || [];
  }, [adminsResponse]);

  const filteredAdmins = useMemo(() => {
    return admins
      .filter((admin: any) =>
        admin?.role?.includes("centralPartnerManager")
      )
      .filter((admin: any) => {
        const fullName = `${admin.first_name} ${admin.last_name}`;
        return fullName.toLowerCase().includes(searchQuery.toLowerCase());
      });
  }, [admins, searchQuery]);

  // ===============================
  // ✅ MAP ADMINS → CATEGORY SHAPE
  // ===============================
  const categories = useMemo(() => {
    return filteredAdmins.map((admin: any) => ({
      category_id: admin._id,
      category_name: `${admin.first_name} ${admin.last_name}`,
      email: admin.email,
      display_picture: admin.display_picture,
      country: admin.country,
      occupation: admin.occupation,
      role: admin.role,
    }));
  }, [filteredAdmins]);

  const handleView = (admin: any) => {
    navigate(`/partners/group/${admin.category_id}`);
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const dumpArtisanData = async () => {
    startLoading();
    try {
      const res: any = await triggerDumpArtisansData().unwrap();
      toast.success(res?.message);
      const url = res?.payload?.data?.fileUrl;
      if (url) window.location.href = url;
      setShowExportModal(false);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || "Export failed");
    } finally {
      stopLoading();
    }
  };

  return (
    <DashboardLayout pageTitle="">
      <div className="bg-[#F2F4F7] min-h-[calc(100vh-80px)]">
        
        {/* HEADER */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <div className="text-[32px] font-semibold text-[#3a3541]">
              Central Partner Managers
            </div>
            <div className="text-base text-[#45556C]">
              {categories.length} Managers
            </div>
          </div>

          <div className="min-w-[300px]">
            <PartInput
              icon={<img src={searchIcon} alt="search" />}
              type="search"
              placeholder="Search managers..."
              handleChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* <Button
            onClick={handleExport}
            classes={[ButtonClass.SOLID, ButtonClass.WITH_ICON]}
            style={{
              backgroundColor: theme.colors.white,
              height: "48px",
              color: "black",
              border: "1.33px solid #0000001A",
            }}
          >
            <img src={exportImg} alt="" />
            Export
          </Button> */}
        </div>

        {/* ANALYTICS */}
        {/* <GlobalAnalytics partner_id={""} /> */}

        {/* STATES */}
        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-xl">
            Failed to load admins
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-4 gap-5 mt-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="p-6 bg-white rounded-xl mt-6 text-center text-gray-500">
            No central partner managers found
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
            {categories.map((admin: any) => (
              <AdminCategoryCard
                key={admin.category_id}
                category={admin}
                onView={handleView}
                onEdit={() => {}}
                onDelete={() => {}}
              />
            ))}
          </div>
        )}
      </div>

      {/* EXPORT MODAL */}
      <AppModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        content={
          <ModalContent
            content1="Export Data"
            content2="Export central partner managers?"
            btnAction={dumpArtisanData}
            linkContent={"Yes, Export"}
            onClick={() => setShowExportModal(false)}
            loading={exportingData}
          />
        }
      />
    </DashboardLayout>
  );
};

export default CentralRebrand;